import {ActionIcon, Button, Paper, TextInput, Tooltip} from "@mantine/core";
import {DataTable} from "mantine-datatable";
import {
    PencilIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    PaperPlaneRightIcon,
    TrashIcon
} from "@phosphor-icons/react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderIndicator from "../../../components/Loader/LoaderIndicator.tsx";
import Page from "../../../components/Page/Page.tsx";
import CGT from "../../../utils/CGT.ts";
import Premium from "../../../utils/Premium.ts";
import {emptyContract} from "../data.js";
import EditContractModal from "./EditContractModal.jsx";
import api, {useUser} from '../../../utils/api.ts';
import {Col, Row} from '../../../components/Flex/Flex';
import SwitchTab from "./SwitchTab.jsx";
import {bodyReward, bodyType} from "./Formatters.jsx";
import useDataTableSessionState from "../../../hooks/useDataTableSessionState.ts";


const bodyExpiration = (row) => {
    if (row.expiration_y > 0 && row.expiration_d >= 0) {
        let now = CGT.now();
        let date = CGT.fromCGT(row.expiration_y, row.expiration_d);
        return <div className={date < now ? 'text-red' : ''}>
            <span>{date.toCGT(true)}</span><br/>
            <small>{date.toRelative()}</small>
        </div>;
    } else {
        return '';
    }
}
const getLastDate = (latest, current) => {
    if (typeof current.date === 'undefined') {
        return latest;
    }
    if (latest === null && current.date) {
        return current.date;
    } else {
        return Math.max(latest, current.date);
    }
}

const bodyScans = row => {
    const {scans} = row;
    if (scans.length === 0) {
        return <i>No scans</i>;
    }
    let last = scans.reduce(getLastDate, null);
    if (last === null) {
        return '';
    }
    let d = CGT.fromSeconds(last);
    return <div><span>{d.toCGT(true)}</span><br/><small>{d.toRelative()}</small></div>
}
const bodyLocations = row => {
    const {locations} = row;
    if (locations.length === 1) {
        const l = locations[0];
        return <span className="text-green" title="Scanned">{l.name} ({l.galX}, {l.galY})</span>
    }
    if (row.sighting) {
        let s = row.sighting.location.system;
        return <span className="text-yellow" title="Sighting">{s.name} ({s.galX}, {s.galY})</span>
    }
    if (row.scans.length === 0) {
        return '';
    }
    if (locations.length === 0 && row?.scans.length > 0) {
        return <i className="text-red">No valid locations</i>;
    }

    return (
        <Tooltip color="gray" position="right" label={<>{locations.map(l => <div key={l.id}>{l.name} ({l.galX}, {l.galY})</div>)}</>}>
            <span>{locations.length}</span>
        </Tooltip>
    );
}

const sortExpire = (event) => {
    const {field, order, data} = event;
    if (field !== 'expiration') {
        return data;
    }
    if (order === 1) {
        return data.sort((a, b) => {
            if (a.expiration_y === b.expiration_y) {
                let ad = parseInt(a.expiration_d);
                let bd = parseInt(b.expiration_d);
                if (isNaN(ad) && isNaN(bd)) {
                    return a.name.localeCompare(b.name);
                }
                return ad === bd ? 0 : (ad > bd ? 1 : -1);
            }
            if (!a.expiration_y) {
                return 1;
            }
            if (!b.expiration_y) {
                return -1;
            }
            return parseInt(a.expiration_y) > parseInt(b.expiration_y) ? 1 : -1;
        });
    } else if (order === -1) {
        return data.sort((a, b) => {
            if (a.expiration_y === b.expiration_y) {
                let ad = parseInt(a.expiration_d);
                let bd = parseInt(b.expiration_d);
                if (isNaN(ad) && isNaN(bd)) {
                    return a.name.localeCompare(b.name);
                }
                return ad === bd ? 0 : (ad < bd ? 1 : -1);
            }
            if (!a.expiration_y) {
                return 1;
            }
            if (!b.expiration_y) {
                return -1;
            }
            return parseInt(a.expiration_y) < parseInt(b.expiration_y) ? 1 : -1;
        });

    }
    return data;
}
const sortLocation = (event) => {
    const {field, order, data} = event;
    if (field !== 'locations') {
        return data;
    }
    if (order === 1) {
        return data.sort((a, b) => {
            if ((a.locations.length === 1 || a.sighting) && (b.locations.length === 1 || b.sighting)) {
                let aname = a.sighting ? a.sighting.name : a.locations[0].name;
                let bname = b.sighting ? b.sighting.name : b.locations[0].name;

                return aname.localeCompare(bname);
            }
            if (a.locations.length === 0) {
                return 1;
            }
            if (b.locations.length === 0) {
                return -1;
            }
            return (a.sighting ? 1 : a.locations.length) > (b.sighting ? 1 : b.locations.length) ? 1 : -1;
        });
    } else if (order === -1) {
        return data.sort((a, b) => {
            if ((a.locations.length === 1 || a.sighting) && (b.locations.length === 1 || b.sighting)) {
                let aname = a.sighting ? a.sighting.name : a.locations[0].name;
                let bname = b.sighting ? b.sighting.name : b.locations[0].name;

                return bname.localeCompare(aname);
            }
            if (a.locations.length === 0) {
                return 1;
            }
            if (b.locations.length === 0) {
                return -1;
            }
            return (a.sighting ? 1 : a.locations.length) < (b.sighting ? 1 : b.locations.length) ? 1 : -1;
        });

    }
    return data;
}



function ContractList() {
    const navigate = useNavigate();
    const [contracts, setContracts] = useState(null);
    const [edit, setEdit] = useState(null);
    const [selected, setSelected] = useState([]);
    const [networks, setNetworks] = useState([]);
    const {sortStatus, setSortStatus, filters, setFilter} = useDataTableSessionState({
        storageKey: 'bh:contracts:list',
        initialSortStatus: {
            columnAccessor: 'expiration',
            direction: 'asc',
        },
        initialFilters: {
            globalFilter: '',
        },
    });
    const globalFilter = typeof filters.globalFilter === 'string' ? filters.globalFilter : '';
    const {user} = useUser()


    const onSave = useCallback(record => {
        setEdit(null);
        setContracts(prev => {
            let next = [...prev];
            let i = next.findIndex(c => c.id === record.id);
            if (i < 0) {
                next.push(record);
            } else {
                next[i] = record;
            }
            return next;
        })
    }, []);

    useEffect(() => {
        api.get('/bh/contracts').then(res => {
            setContracts(res.data);
        })
        api.get('/data/factions/bh').then(res => {
            setNetworks(res.data.sort((a, b) => a.name.localeCompare(b.name)));
        })
    }, []);

    const filteredRecords = useMemo(() => {
        if (!contracts) return [];
        if (!globalFilter) return contracts;
        const lower = globalFilter.toLowerCase();
        return contracts.filter(r =>
            r.name?.toLowerCase().includes(lower) ||
            r.notes?.toLowerCase().includes(lower)
        );
    }, [contracts, globalFilter]);

    const sortedRecords = useMemo(() => {
        const accessor = sortStatus.columnAccessor;
        const order = sortStatus.direction === 'asc' ? 1 : -1;
        const data = [...filteredRecords];

        if (accessor === 'expiration') {
            return sortExpire({field: 'expiration', order, data});
        }
        if (accessor === 'locations') {
            return sortLocation({field: 'locations', order, data});
        }

        return data.sort((a, b) => {
            const aVal = a[accessor] ?? '';
            const bVal = b[accessor] ?? '';
            const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal - bVal);
            return order === 1 ? cmp : -cmp;
        });
    }, [filteredRecords, sortStatus]);


    const bodyActions = useCallback(row => {
        return (
            <Row mb={0}>
                <ActionIcon variant="light" radius="xl" onClick={() => setEdit(row)} title="Edit Contract">
                    <PencilIcon size={16}/>
                </ActionIcon>
                <ActionIcon variant="light" radius="xl" onClick={() => {
                    if (confirm('Are you sure you want to delete this contract? You will lose all scan data')) {
                        api.delete('/bh/contract/' + row.id).then(() => {
                            setContracts(prev => {
                                let next = [...prev];
                                return next.filter(c => c.id !== row.id);
                            })
                        })
                    }
                }} title="Delete Contract">
                    <TrashIcon size={16}/>
                </ActionIcon>
            </Row>
        )
    }, []);
    const bodyName = useCallback(row => {
        return <Link className="contract-name" to={`/bh/contract/${row.id}`}>{row.name}</Link>;
    }, []);
    const title = useMemo(() => user.subLevel > 0 ?
            <h2 className="m-0">Active Contracts ({contracts?.length})</h2> :
            <h2 className="m-0">Active Contracts ({contracts?.length}/{Premium.ACTIVE_CONTRACTS})</h2>
        , [user.subLevel, contracts?.length]);

    const onGlobalFilter = useCallback(e => {
        setFilter('globalFilter', e.target.value ?? '');
    }, [setFilter]);

    if (contracts === null) {
        return (<Page>
            <LoaderIndicator id="page-load" message="Loading contracts"/>
        </Page>)
    }

    return (
        <Page>
            <title>Contracts</title>
            <SwitchTab
                left={title}
                right={<Button disabled={user.subLevel === 0 && contracts.length >= Premium.ACTIVE_CONTRACTS} leftSection={<PlusIcon size={16}/>}
                               onClick={() => setEdit(emptyContract)}>New Contract</Button>}
            />
            <Paper>
                <Row mb={4}>
                    <Col grow={1}>
                        <Button leftSection={<PaperPlaneRightIcon size={16}/>} disabled={selected.length === 0}
                                onClick={() => navigate('/bh/contracts/transfer', {state: {contracts: selected}})}>Transfer selected contracts</Button>
                    </Col>
                    <Col>
                        <TextInput
                            leftSection={<MagnifyingGlassIcon size={16}/>}
                            value={globalFilter}
                            onChange={onGlobalFilter}
                            placeholder="Search"
                        />
                    </Col>
                </Row>
                <DataTable
                    records={sortedRecords}
                    idAccessor="id"
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                    selectedRecords={selected}
                    onSelectedRecordsChange={setSelected}
                    columns={[
                        {accessor: 'name', title: 'Target Name', sortable: true, render: bodyName},
                        {accessor: 'expiration', title: 'Expiration', sortable: true, render: bodyExpiration},
                        {accessor: 'lastScan', title: 'Last scan', render: bodyScans},
                        {accessor: 'locations', title: 'Possible Locations', sortable: true, render: bodyLocations},
                        {accessor: 'difficulty', title: 'Difficulty'},
                        {accessor: 'type', title: 'Type', render: bodyType},
                        {accessor: 'reward', title: 'Reward', render: bodyReward},
                        {accessor: 'notes', title: 'Notes', sortable: true},
                        {accessor: 'actions', title: 'Actions', render: bodyActions},
                    ]}
                />
            </Paper>
            {edit !== null ?
                <EditContractModal edit={edit} setEdit={setEdit} onHide={() => setEdit(null)} onSave={onSave} networks={networks}/>
                : null}
        </Page>
    );


}

export default withRouteErrorBoundary(ContractList);
export {bodyExpiration, sortExpire};
